import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import {
  DeviceIds,
  DeviceRoles,
  EssSystemType,
  Feature,
  FeatureNames,
  GatewayCommands,
  GatewayProperties,
  HeatingConfigurationRegulationMode,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  RoomsCommands,
  SummaryConsumptionProperties,
} from '@vi-iot/client';

import {
  AuthStore,
  defaultPanelsOrder,
  DevicesStore,
  RichDevice,
  FeatureService,
  FEATURES_PANEL_TYPES,
  InstallationDetailsStore,
  InstallationPanelOrder,
  isReady,
  InstallationFeaturesStore,
} from '@store';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ActivatedRoute } from '@angular/router';
import { InstallationService } from '../../../services/installation.service';
import { DeviceFeatureTilesBase } from '../device-feature-tiles-base';
import { DragDropLayoutService } from '../../drag-drop-layout/drag-drop-layout.service';
import { TranslateService } from '@ngx-translate/core';
import { ElectricHeaterService } from '../../electric-heater/electric-heater.service';

@Component({
  selector: 'app-default-feature-tiles',
  templateUrl: './default-feature-tiles.component.html',
  styleUrls: ['./default-feature-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultFeatureTilesComponent
  extends DeviceFeatureTilesBase
  implements OnInit, OnDestroy {
  isSystemPanelVisible$: Observable<boolean>;
  status$: Observable<Feature<GatewayProperties, GatewayCommands>>;
  rooms$: Observable<Feature<IsEnabledProperty, RoomsCommands>>;
  bufferTank$: Observable<Feature<IsStringValueProperty, never>>;
  gasConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  gasConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  autarkySummary$: Observable<Feature<SummaryConsumptionProperties, never>>;
  gasConsumptionHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerCumulativeProducedFeature$: Observable<Feature<IsNumberValueProperty, never>>;
  isEssBatteryOrHybrid$: Observable<boolean>;
  deviceSelectId: string;
  deviceId: string;
  gatewaySerialOfSelectedDevice: string;
  renderFlag: boolean;
  isRsaUser$: Observable<boolean>;
  isVitopureDevice$: Observable<boolean>;
  panels: InstallationPanelOrder[];
  panelTypes = FEATURES_PANEL_TYPES;
  isDragDropDisabled: boolean;
  gatewaySerial: string;
  isVicareThermostatMode$: Observable<boolean>;
  tooltipInfo: string;
  device: RichDevice;

  readonly roomOnOff = HeatingConfigurationRegulationMode.DeltaTRoomOnOff;
  readonly roomModulation = HeatingConfigurationRegulationMode.DeltaTRoomModulation;
  readonly essSystemType = EssSystemType;
  readonly DeviceRoles = DeviceRoles;

  @Input() installationId: number;
  @Input() isCascadeInstallation: boolean;
  @Input() isLegacyDevice: boolean;

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore,
    public featureService: FeatureService,
    public installationService: InstallationService,
    public electricHeaterService: ElectricHeaterService,
    private cdRef: ChangeDetectorRef,
    private authStore: AuthStore,
    private dragDropLayoutService: DragDropLayoutService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService
  ) {
    super(
      installationDetailsStore,
      devicesStore,
      featureService,
      installationService,
      installationFeaturesStore
    );
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.gatewaySerial = queryParams?.gatewaySerial;
    });
    this.dragDropLayoutService.isDragDropDisabled
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isDragDropDisabled) => {
        this.isDragDropDisabled = isDragDropDisabled;
        this.cdRef.markForCheck();
      });

    this.initializeGeneralTiles();

    this.devicesStore.selectedDevice$
      .pipe(
        filter((device) => !!device),
        distinctUntilChanged(
          (prev: RichDevice, curr: RichDevice) => prev.selectId === curr.selectId
        ),
        switchMap((device) => {
          this.deviceSelectId = device.selectId;
          this.deviceId = device.id;
          this.gatewaySerialOfSelectedDevice = device.gatewaySerial;
          this.device = device;

          super.initializeDeviceFeatures(this.deviceSelectId, device.id);

          return this.installationDetailsStore.getPanelsOrderForDevice(device.gatewaySerial);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((panelsOrder: InstallationPanelOrder[]) => {
        if (panelsOrder?.length > 0) {
          this.panels = panelsOrder;
        } else {
          this.panels = defaultPanelsOrder;
        }
      });

    this.installationDetailsStore
      .getPanelsOrderForDevice(this.gatewaySerialOfSelectedDevice)
      .pipe(filter(() => !this.panels))
      .subscribe(() => {
        this.panels = defaultPanelsOrder;
        this.cdRef.markForCheck();
      });

    this.gasConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryHeating
    );
    this.gasConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryDhw
    );
    this.powerConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryHeating
    );
    this.powerConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryDhw
    );
    this.autarkySummary$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingPowerProductionDemandCoverageRaw
    );
    this.gasConsumptionHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionHeating
    );
    this.powerConsumptionHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionHeating
    );

    this.powerCumulativeProducedFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCumulativeProduced
    );
    this.isRsaUser$ = this.authStore.isRsaUser();
    this.bufferTank$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.bufferCylinderOperatingModes
    );
    this.gatewayStatus$ = this.installationDetailsStore.getGatewayStatus();
    this.isVitopureDevice$ = this.installationDetailsStore.hasSelectedDeviceGivenRoles([
      DeviceRoles.ProductVitopure,
    ]);
    this.isVicareThermostatMode$ = this.installationFeaturesStore.isVicareThermostatMode();
    this.isEssBatteryOrHybrid$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.essSystemType)
      .pipe(
        filter(
          (feature: Feature<IsStringValueProperty, never>) =>
            isReady(feature) && !!feature?.properties?.value?.value
        ),
        map((feature: Feature<IsStringValueProperty, never>) => {
          const systemType = feature.properties.value.value;

          return (
            systemType === this.essSystemType.battery || systemType === this.essSystemType.hybrid
          );
        })
      );
    this.tooltipInfo = this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.OVERLAY.CONSUMPTION_TOOLTIP'
    );
  }

  private initializeGeneralTiles(): void {
    this.isSystemPanelVisible$ = this.featureService.selectIsSystemPanelVisible();
    this.status$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayStatus
    );
    this.rooms$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.rooms,
      DeviceIds.RoomControl
    );
  }

  drop(event: CdkDragDrop<string>): void {
    this.dragDropLayoutService.updatePanelOrderListAfterDrop(
      event.previousContainer.data,
      event.container.data,
      this.panels,
      this.gatewaySerialOfSelectedDevice
    );
  }

  trackBy(_index: number, panel: InstallationPanelOrder): string {
    return panel.panel;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
